import React from "react";
import { shallowEqual } from "react-redux";
import { useUscSelector } from "../../../../../../redux/store";
import { IntegratedEmissionLabel } from "../../../../../../../../common-deprecated/components/emission-label/IntegratedEmissionLabel";
import { ecolabelDisclaimerSelector } from "../../../../../../utils/disclaimerUtils";
import { ecolabelFuelTypeValuesSelector, legalSectionSpecsSelector } from "../../../../../../utils/specUtils";
import { useUscLabel, useUscOptionalLabel } from "../../../../../../utils/labels";
import SpecItem from "../common/SpecItem";
import Text from "../../../../../../../shared-components/toyota/styles/atoms/Text";
import * as Styles from "./SectionLegal.styles";
import { DfFuelTypeId } from "../../../../../../../shared-logic/utils/dfConstants";
import { aemFuelTypeToDfFuelTypeMap } from "../../../../../../../shared-logic/types/UscCommonTypes";
import { formatEcoCostsData, formatEcoCostsLabels } from "../../../../../../utils/ecoCostsUtils";

/**
 * UC-561
 * Custom PDP section for TDG which contains
 * all relevant legal information (in the form of specs) for the vehicle
 */
const SectionLegal = (): JSX.Element | null => {
    const vehicle = useUscSelector((state) => state.vehicle);
    const fuelTypeValues = useUscSelector(ecolabelFuelTypeValuesSelector, shallowEqual);
    const specs = useUscSelector(legalSectionSpecsSelector, shallowEqual);
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const tmefConfig = useUscSelector((state) => state.uscSettings.ecolabelTmefConfig);
    const tmefData = useUscSelector((state) => state.ecoCosts.data);
    const [fuelTypeLabel, otherFuelTypeLabel, efficiencyClassTitleLabel, yearPlaceholderLabel, taxExemptLabel] =
        useUscLabel([
            "uscPdpEcolabelFuelType",
            "uscPdpEcolabelOtherFuelType",
            "uscPdpEcolabelEfficiencyClassTitle",
            "uscPdpEcolabelYearPlaceholder",
            "uscPdpEcolabelTaxExempt",
        ]);
    const efficiencyClassDescriptionLabel = useUscOptionalLabel("uscPdpEcolabelEfficiencyClassDescription");
    const vehicleFuelType = vehicle.result?.product.engine.marketingFuelType.code;

    const disclaimers = useUscSelector(ecolabelDisclaimerSelector(vehicleFuelType), shallowEqual);

    // Labels used in the IntegratedEmissionLabel component
    const [emissionLabelCombined, emissionLabelWeightedCombined, emissionLabelChargeSustaining] = useUscLabel([
        "uscPdpEcolabelCombined",
        "uscPdpEcoLabelWeightedCombined",
        "uscPdpEcolabelWithDischargedBattery",
    ]);

    const emissionLabelValueLabel =
        vehicleFuelType === DfFuelTypeId.PluginHybrid ? emissionLabelWeightedCombined : emissionLabelCombined;

    const fuelTypeName = aemFuelTypeToDfFuelTypeMap[vehicleFuelType as DfFuelTypeId];
    const tmefConfigForFuelType = tmefConfig[fuelTypeName];
    const ecoCostsLabels = formatEcoCostsLabels(commonSettings, tmefData, tmefConfigForFuelType);
    const formattedEcoCostsData = formatEcoCostsData(commonSettings, tmefData, yearPlaceholderLabel);

    return (
        <Styles.Wrapper>
            <Styles.List>
                {fuelTypeValues.fuelType && <SpecItem title={fuelTypeLabel} value={fuelTypeValues.fuelType} />}
                {fuelTypeValues.otherFuelType && (
                    <SpecItem title={otherFuelTypeLabel} value={fuelTypeValues.otherFuelType} />
                )}
            </Styles.List>
            <Styles.List>
                {specs.ecCombined && (
                    <SpecItem
                        title={specs.ecCombined.title}
                        value={specs.ecCombined.value}
                        unit={specs.ecCombined.unit}
                        tooltipReference={specs.ecCombined.tooltipReference}
                    />
                )}
                {specs.fcWltpCombined && (
                    <SpecItem
                        title={specs.fcWltpCombined.title}
                        value={specs.fcWltpCombined.value}
                        unit={specs.fcWltpCombined.unit}
                        tooltipReference={specs.fcWltpCombined.tooltipReference}
                    />
                )}
                {specs.fcCsCombined && (
                    <SpecItem
                        title={specs.fcCsCombined.title}
                        value={specs.fcCsCombined.value}
                        unit={specs.fcCsCombined.unit}
                        tooltipReference={specs.fcCsCombined.tooltipReference}
                    />
                )}
                {specs.co2WltpCombined && (
                    <SpecItem
                        title={specs.co2WltpCombined.title}
                        value={specs.co2WltpCombined.value}
                        unit={specs.co2WltpCombined.unit}
                        tooltipReference={specs.co2WltpCombined.tooltipReference}
                        footnoteNumber={1}
                    />
                )}
                {specs.co2CsCombined && (
                    <SpecItem
                        title={specs.co2CsCombined.title}
                        value={specs.co2CsCombined.value}
                        unit={specs.co2CsCombined.unit}
                        tooltipReference={specs.co2CsCombined.tooltipReference}
                        footnoteNumber={1}
                    />
                )}
                {specs.eaerCombined && (
                    <SpecItem
                        title={specs.eaerCombined.title}
                        value={specs.eaerCombined.value}
                        unit={specs.eaerCombined.unit}
                        tooltipReference={specs.eaerCombined.tooltipReference}
                    />
                )}
                {specs.perCombined && (
                    <SpecItem
                        title={specs.perCombined.title}
                        value={specs.perCombined.value}
                        unit={specs.perCombined.unit}
                        tooltipReference={specs.perCombined.tooltipReference}
                    />
                )}
            </Styles.List>
            <Styles.List>
                {/* I wrapped both titles inside a `div` to ignore the gap between the specItems that is defined in the list */}
                <div>
                    <Text tag="h2" variant="body-small-book">
                        {efficiencyClassTitleLabel}
                    </Text>
                    {efficiencyClassDescriptionLabel && (
                        <Text tag="h3" variant="body-small-book">
                            {efficiencyClassDescriptionLabel}
                        </Text>
                    )}
                </div>
                {specs.efficiencyClass && (
                    <Styles.IntegratedEmissionlabelWrapper>
                        <IntegratedEmissionLabel
                            value={specs.efficiencyClass.value}
                            // Gets shown for every vehicle, regardless of fuelType
                            valueLabel={emissionLabelValueLabel}
                            // Only relevant for PHEV vehicles
                            batteryValue={specs.efficiencyClassCSMode?.value || ""}
                            batteryValueLabel={emissionLabelChargeSustaining}
                        />
                    </Styles.IntegratedEmissionlabelWrapper>
                )}
                {specs.ecolabelEcCombined && (
                    <SpecItem
                        variant="body-small-semibold"
                        color="primary-black"
                        title={specs.ecolabelEcCombined.title}
                        value={specs.ecolabelEcCombined.value}
                        unit={specs.ecolabelEcCombined.unit}
                        tooltipReference={specs.ecolabelEcCombined.tooltipReference}
                    />
                )}
                {specs.ecInnerCity && (
                    <SpecItem
                        title={specs.ecInnerCity.title}
                        value={specs.ecInnerCity.value}
                        unit={specs.ecInnerCity.unit}
                        tooltipReference={specs.ecInnerCity.tooltipReference}
                    />
                )}
                {specs.ecOutskirts && (
                    <SpecItem
                        title={specs.ecOutskirts.title}
                        value={specs.ecOutskirts.value}
                        unit={specs.ecOutskirts.unit}
                        tooltipReference={specs.ecOutskirts.tooltipReference}
                    />
                )}
                {specs.ecCountryRoad && (
                    <SpecItem
                        title={specs.ecCountryRoad.title}
                        value={specs.ecCountryRoad.value}
                        unit={specs.ecCountryRoad.unit}
                        tooltipReference={specs.ecCountryRoad.tooltipReference}
                    />
                )}
                {specs.ecHighway && (
                    <SpecItem
                        title={specs.ecHighway.title}
                        value={specs.ecHighway.value}
                        unit={specs.ecHighway.unit}
                        tooltipReference={specs.ecHighway.tooltipReference}
                    />
                )}
                {specs.ecolabelFcWltpCombined && (
                    <SpecItem
                        variant="body-small-semibold"
                        color="primary-black"
                        title={specs.ecolabelFcWltpCombined.title}
                        value={specs.ecolabelFcWltpCombined.value}
                        unit={specs.ecolabelFcWltpCombined.unit}
                        tooltipReference={specs.ecolabelFcWltpCombined.tooltipReference}
                    />
                )}
                {specs.ecolabelFcCsCombined && (
                    <SpecItem
                        variant="body-small-semibold"
                        color="primary-black"
                        title={specs.ecolabelFcCsCombined.title}
                        value={specs.ecolabelFcCsCombined.value}
                        unit={specs.ecolabelFcCsCombined.unit}
                        tooltipReference={specs.ecolabelFcCsCombined.tooltipReference}
                    />
                )}
                {specs.fcInnerCity && (
                    <SpecItem
                        title={specs.fcInnerCity.title}
                        value={specs.fcInnerCity.value}
                        unit={specs.fcInnerCity.unit}
                        tooltipReference={specs.fcInnerCity.tooltipReference}
                    />
                )}
                {specs.fcOutskirts && (
                    <SpecItem
                        title={specs.fcOutskirts.title}
                        value={specs.fcOutskirts.value}
                        unit={specs.fcOutskirts.unit}
                        tooltipReference={specs.fcOutskirts.tooltipReference}
                    />
                )}
                {specs.fcCountryRoad && (
                    <SpecItem
                        title={specs.fcCountryRoad.title}
                        value={specs.fcCountryRoad.value}
                        unit={specs.fcCountryRoad.unit}
                        tooltipReference={specs.fcCountryRoad.tooltipReference}
                    />
                )}
                {specs.fcHighway && (
                    <SpecItem
                        title={specs.fcHighway.title}
                        value={specs.fcHighway.value}
                        unit={specs.fcHighway.unit}
                        tooltipReference={specs.fcHighway.tooltipReference}
                    />
                )}
            </Styles.List>
            {ecoCostsLabels && !!Object.keys(ecoCostsLabels).length && (
                <Styles.List>
                    {ecoCostsLabels.annualEnergyCostTitle && formattedEcoCostsData.yearlyCostEuros && (
                        <SpecItem
                            title={ecoCostsLabels.annualEnergyCostTitle}
                            value={formattedEcoCostsData.yearlyCostEuros}
                            variant="body-small-semibold"
                            color="primary-black"
                        />
                    )}
                    {ecoCostsLabels.annualEnergyCost && (
                        <Text tag="div" variant="body-small-book">
                            {ecoCostsLabels.annualEnergyCost}
                        </Text>
                    )}
                    {ecoCostsLabels.co2CostTitle && (
                        <Text tag="div" variant="body-small-semibold" color="primary-black">
                            {ecoCostsLabels.co2CostTitle}
                            <Styles.Sup>2</Styles.Sup>
                        </Text>
                    )}
                    {ecoCostsLabels.lowCo2Price && formattedEcoCostsData.co2CostLow && (
                        <SpecItem
                            title={ecoCostsLabels.lowCo2Price}
                            value={formattedEcoCostsData.co2CostLow}
                            variant="body-small-semibold"
                            color="primary-black"
                        />
                    )}
                    {ecoCostsLabels.mediumCo2Price && formattedEcoCostsData.co2CostMedium && (
                        <SpecItem title={ecoCostsLabels.mediumCo2Price} value={formattedEcoCostsData.co2CostMedium} />
                    )}
                    {ecoCostsLabels.highCo2Price && formattedEcoCostsData.co2CostHigh && (
                        <SpecItem title={ecoCostsLabels.highCo2Price} value={formattedEcoCostsData.co2CostHigh} />
                    )}
                    {ecoCostsLabels.vehicleTaxTitle && formattedEcoCostsData.taxYearly && (
                        <>
                            {vehicleFuelType === DfFuelTypeId.Electric || vehicleFuelType === DfFuelTypeId.Hydrogen ? (
                                <SpecItem
                                    title={ecoCostsLabels.vehicleTaxTitle}
                                    value={taxExemptLabel}
                                    variant="body-small-semibold"
                                    color="primary-black"
                                    footnoteNumber={3}
                                />
                            ) : (
                                <SpecItem
                                    title={ecoCostsLabels.vehicleTaxTitle}
                                    value={formattedEcoCostsData.taxYearly}
                                    variant="body-small-semibold"
                                    color="primary-black"
                                />
                            )}
                        </>
                    )}
                </Styles.List>
            )}
            {!!disclaimers.length && (
                <Styles.List>
                    {disclaimers.map(({ value, containsHtml, number }, index) =>
                        containsHtml ? (
                            // eslint-disable-next-line react/no-array-index-key
                            <Styles.Footnote key={index}>
                                {number && <Styles.FootnoteNumber>{number}.</Styles.FootnoteNumber>}

                                <div dangerouslySetInnerHTML={{ __html: value }} />
                            </Styles.Footnote>
                        ) : (
                            // eslint-disable-next-line react/no-array-index-key
                            <Styles.Footnote key={index}>
                                {number && <Styles.FootnoteNumber>{number}.</Styles.FootnoteNumber>}

                                {value}
                            </Styles.Footnote>
                        ),
                    )}
                </Styles.List>
            )}
        </Styles.Wrapper>
    );
};

export default SectionLegal;
